/**
 * The public signer API (app/authorised_requests/signatures/public_api.py). No sign-in: plain fetch, never the
 * app's axios (whose interceptor attaches the session token). The link token lives in the URL FRAGMENT
 * (/app/sign#t=…), which browsers never send, and rides to the API in a header — so no server or load-balancer
 * log ever records it.
 */
import { getApiUrl } from "../config/product";

const HEADER = "X-Aeronet-Sign-Token";
const SESSION_HEADER = "X-Aeronet-Sign-Session";

// The verified-code session. MEMORY ONLY: never localStorage, sessionStorage or the URL, so a reload (or a new
// tab) asks for a new code. The server issues it from POST /verify and accepts it for 30 minutes.
// It is bound to the link token it was issued for and is only ever sent with that token.
let session: { token: string; value: string } | null = null;

/** A failed public sign call: the server's message, the HTTP status and its machine code (e.g. OTP_REQUIRED). */
export class SignApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "SignApiError";
    this.status = status;
    this.code = code;
  }
}

export const clearSession = (): void => {
  session = null;
};

export interface SignView {
  // title and signer_name are null for a Contract Sign link until its code is verified (the server withholds
  // them); an Approval link has them at once.
  title: string | null;
  kind: "contract_sign" | "approval";
  signer_name: string | null;
  needs_otp: boolean;
  verified: boolean;
  // true once the link has had 5 wrong codes (only the sender can re-issue it); always false for an Approval
  // link. An older backend omits it: read a missing value as false.
  locked: boolean;
}

export function tokenFromHash(hash: string = window.location.hash): string | null {
  const match = /[#&]t=([A-Za-z0-9_-]+)/.exec(hash || "");
  return match ? match[1] : null;
}

async function call(path: string, token: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(`${getApiUrl()}/v6/public/sign${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      ...(init.headers || {}),
      [HEADER]: token,
      ...(session && session.token === token ? { [SESSION_HEADER]: session.value } : {}),
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    let message = "Something went wrong. Please try the link again.";
    let code: string | undefined;
    try {
      const detail = (await response.json())?.detail;
      message = detail?.message ?? message;
      code = typeof detail?.code === "string" ? detail.code : undefined;
    } catch {
      /* not JSON */
    }
    throw new SignApiError(message, response.status, code);
  }
  return response;
}

export const view = async (token: string): Promise<SignView> => (await call("", token)).json();

export const sendOtp = async (token: string): Promise<void> => {
  await call("/otp", token, { method: "POST" });
};

export const verifyOtp = async (token: string, code: string): Promise<void> => {
  const response = await call("/verify", token, { method: "POST", body: JSON.stringify({ code }) });
  let issued: string | null = null;
  try {
    // An older backend answers 204 with no body: tolerate it (no session, the next call is refused by the server).
    const body = (await response.json()) as { session?: unknown } | null;
    issued = typeof body?.session === "string" && body.session ? body.session : null;
  } catch {
    /* empty or non-JSON body */
  }
  session = issued ? { token, value: issued } : null;
};

export const resetSessionForTests = (): void => clearSession();

export const pdfFile = async (token: string, title: string | null): Promise<File> => {
  const blob = await (await call("/pdf", token)).blob();
  return new File([blob], `${title || "Document"}.pdf`, { type: "application/pdf" });
};

export const sign = async (
  token: string,
  body: { typed_name: string; typed_title: string; accept: boolean }
): Promise<void> => {
  await call("/sign", token, { method: "POST", body: JSON.stringify(body) });
  clearSession();
};

export const approve = async (token: string, comment: string): Promise<void> => {
  await call("/approve", token, { method: "POST", body: JSON.stringify({ comment }) });
  clearSession();
};

/** A decline carries its reason, which the sender is shown (the Declined mail and the request's record). */
export const decline = async (token: string, reason: string): Promise<void> => {
  await call("/decline", token, { method: "POST", body: JSON.stringify({ reason }) });
  clearSession();
};

// ----- a batch link (one link and one code for several documents; batch.py) ----------------------------------
// The single routes answer 409 USE_BATCH_LINK for such a link and the page moves to these. The session is the
// same memory-only value; a batch decision does NOT clear it, because the signer carries on with the others.

export interface BatchDocument {
  n: number;
  /** null until the code is verified (Contract Sign), as the single view's title. */
  title: string | null;
  /** This signer's step on the document: Open (theirs to decide), Signed / Approved / Declined, Pending, Voided. */
  state: string;
  /** The signer has opened it; "Sign all" / "Approve all" need every open one reviewed. */
  reviewed: boolean;
  comment: string | null;
}

export interface BatchView extends SignView {
  batch: true;
  documents: BatchDocument[];
}

export const isBatchLink = (e: unknown): boolean => (e as SignApiError | undefined)?.code === "USE_BATCH_LINK";

export const batchView = async (token: string): Promise<BatchView> => (await call("/batch", token)).json();

export const batchSendOtp = async (token: string): Promise<void> => {
  await call("/batch/otp", token, { method: "POST" });
};

export const batchVerifyOtp = async (token: string, code: string): Promise<void> => {
  const body = (await (
    await call("/batch/verify", token, { method: "POST", body: JSON.stringify({ code }) })
  ).json()) as {
    session?: unknown;
  };
  session = typeof body?.session === "string" && body.session ? { token, value: body.session } : null;
};

export const batchPdfFile = async (token: string, n: number, title: string | null): Promise<File> => {
  const blob = await (await call(`/batch/documents/${n}/pdf`, token)).blob();
  return new File([blob], `${title || `Document ${n}`}.pdf`, { type: "application/pdf" });
};

/** n = one document, null = every open one (the server refuses NOT_REVIEWED until each was opened). */
export const batchSign = async (
  token: string,
  n: number | null,
  body: { typed_name: string; typed_title: string; accept: boolean }
): Promise<number> => {
  const path = n === null ? "/batch/sign-all" : `/batch/documents/${n}/sign`;
  return (
    (await (await call(path, token, { method: "POST", body: JSON.stringify(body) })).json()) as { decided: number }
  ).decided;
};

export const batchApprove = async (token: string, n: number | null, comment: string): Promise<number> => {
  const path = n === null ? "/batch/approve-all" : `/batch/documents/${n}/approve`;
  return (
    (await (await call(path, token, { method: "POST", body: JSON.stringify({ comment }) })).json()) as {
      decided: number;
    }
  ).decided;
};

/** Declines document n only; the others carry on. */
export const batchDecline = async (token: string, n: number, reason: string): Promise<void> => {
  await call(`/batch/documents/${n}/decline`, token, { method: "POST", body: JSON.stringify({ reason }) });
};
